import { useMemo } from 'react';

import { useGpuPrerequisites } from './useGpuPrerequisites';
import { mapTopMostWorkloadsGpuWasteDatapoints } from '@cast/split-me/features/cluster/cost-report';
import { useWorkloadsGpuWasteCostImpactQuery } from '@cast/split-me/hooks';
import {
  TopMostExpensiveGroup,
  TopMostExpensiveGroupDatapoint,
} from '@cast/split-me/types';

import { useGpuInventoryDateRange } from './useGpuInventoryDateRange';

const PAGE_LIMIT = 10;

export type UseGpuIdleSpendDataResult = {
  topItems: TopMostExpensiveGroup[];
  datapoints: TopMostExpensiveGroupDatapoint;
  isLoading: boolean;
  isFetching: boolean;
  isCapabilitiesLoading: boolean;
  hasGpuMetrics: boolean;
  hasGpuData: boolean;
  isDataProcessing: boolean;
  isGpuExporterInstalled: boolean;
  error: unknown;
  refetch: () => void;
};

export const useGpuIdleSpendData = (): UseGpuIdleSpendDataResult => {
  const { currentDateRange, adjustedDateRange, timeZone } =
    useGpuInventoryDateRange();
  const {
    clusterId,
    hasGpuMetrics,
    hasGpuData,
    isDataProcessing,
    isGpuExporterInstalled,
    isCapabilitiesLoading,
    error: prerequisitesError,
    refetch: refetchPrerequisites,
  } = useGpuPrerequisites({
    startTime: adjustedDateRange[0].toISOString(),
    endTime: adjustedDateRange[1].toISOString(),
  });

  const [startTime, endTime] = [
    adjustedDateRange[0].toISOString(),
    adjustedDateRange[1].toISOString(),
  ];

  const {
    data: workloads,
    isLoading: isQueryLoading,
    isFetching: isQueryFetching,
    error: queryError,
    refetch: refetchQuery,
  } = useWorkloadsGpuWasteCostImpactQuery({
    clusterIds: [clusterId],
    startTime,
    endTime,
    pageLimit: PAGE_LIMIT,
    useListingPrices: false,
    enabled: !!clusterId && hasGpuMetrics,
  });

  const { topWorkloadsGpuWaste, topWorkloadsGpuWasteDatapoints } = useMemo(
    () =>
      mapTopMostWorkloadsGpuWasteDatapoints(
        currentDateRange,
        workloads ?? [],
        timeZone
      ),
    [currentDateRange, workloads, timeZone]
  );

  return {
    topItems: topWorkloadsGpuWaste,
    datapoints: topWorkloadsGpuWasteDatapoints,
    isLoading: isCapabilitiesLoading || isQueryLoading,
    isFetching: isCapabilitiesLoading || isQueryFetching,
    isCapabilitiesLoading,
    hasGpuMetrics,
    hasGpuData,
    isDataProcessing,
    isGpuExporterInstalled,
    error: prerequisitesError || queryError,
    refetch: () => {
      refetchPrerequisites();
      refetchQuery?.();
    },
  };
};
